import type { ISearchFilter } from 'providers/GlobalProvider';

export type EntityFilterKey =
  | 'entityType'
  | 'country'
  | 'lensCode'
  | 'matchSource';

export type EntityTableFilterSelection = Record<EntityFilterKey, string[]>;

/**
 * The state needed to reproduce the matches currently shown by the Entity
 * Screening table in a CSV export.  Keep this separate from the screen
 * filters: those belong to the original screen request, while these values
 * are owned by the results table itself.
 */
export type EntityTableExportContext = {
  activeTab: 'All Matches' | 'Ownership';
  searchQuery: string;
  tableFilters: EntityTableFilterSelection;
};

export const EMPTY_ENTITY_TABLE_FILTERS: EntityTableFilterSelection = {
  entityType: [],
  country: [],
  lensCode: [],
  matchSource: [],
};

/**
 * The `tableFilters` half of an entity/ownership `scoreParams` payload, or
 * `undefined` when nothing is selected.
 *
 * The entity service applies these as an OpenSearch `post_filter`, so Hit
 * Total and the pager describe the filtered set (rather than the matches that
 * happen to sit on the loaded page) while the facet aggregations behind the
 * Filters modal keep listing every value in the screen.
 *
 * `lensCode` carries full `BITE Lens` names — the table shows abbreviations,
 * but the query needs the real values.
 */
export const buildEntityTableFilterParams = (
  filters?: EntityTableFilterSelection | null,
):
  | {
      entityType?: string[];
      country?: string[];
      lens?: string[];
      matchSource?: string[];
    }
  | undefined => {
  if (!filters) {
    return undefined;
  }

  const clean = (values?: string[]) => {
    const kept = (values || [])
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    return kept.length > 0 ? kept : undefined;
  };

  const params = {
    entityType: clean(filters.entityType),
    country: clean(filters.country),
    lens: clean(filters.lensCode),
    matchSource: clean(filters.matchSource),
  };

  return Object.values(params).some(Boolean) ? params : undefined;
};

export const countEntityTableFilters = (
  filters: EntityTableFilterSelection,
): number =>
  Object.values(filters).reduce((total, values) => total + values.length, 0);

export const hasEntityTableFilters = (
  filters: EntityTableFilterSelection,
): boolean => countEntityTableFilters(filters) > 0;

export const entityTableFiltersEqual = (
  a: EntityTableFilterSelection,
  b: EntityTableFilterSelection,
): boolean =>
  (Object.keys(EMPTY_ENTITY_TABLE_FILTERS) as EntityFilterKey[]).every(
    (key) =>
      a[key].length === b[key].length &&
      a[key].every((value) => b[key].includes(value)),
  );

/**
 * Table Filters as stored on a `SearchFilter`. `lenses` / `lists` are the
 * production field names, so filters saved before Table Filters keep their
 * lens and list selections.
 */
export const toSavedEntityFilterFields = (
  selection: EntityTableFilterSelection,
) => ({
  lenses: selection.lensCode,
  lists: selection.matchSource,
  entityCountries: selection.country,
  entityTypes: selection.entityType,
});

/**
 * The pressed Saved Filter. `selection` is what was applied for the filter,
 * not its stored fields: after "Update saved" the parent's copy of the filter
 * lags until the saved filter list reloads.
 */
export type ActiveSavedEntityFilter = {
  filter: ISearchFilter;
  selection: EntityTableFilterSelection;
  hasLegacyParts: boolean;
};

/**
 * Production saved filters also carry listing `countries` and `agencies`,
 * which narrowed the screen before it ran and have no Table Filter
 * equivalent; `hasLegacyParts` says some of the filter was not applied.
 */
export const fromSavedEntityFilter = (
  filter: Partial<ISearchFilter> | null,
): { selection: EntityTableFilterSelection; hasLegacyParts: boolean } => ({
  selection: {
    entityType: filter?.entityTypes || [],
    country: filter?.entityCountries || [],
    lensCode: filter?.lenses || [],
    matchSource: filter?.lists || [],
  },
  hasLegacyParts: Boolean(
    filter?.countries?.length || filter?.agencies?.length,
  ),
});
